import {CompanyLegalForm, ICivilPartnershipPartner, ILegalEntity, LegalEntity, LegalEntityType} from './legal-entity.model';
import {ObjectList} from './interface/object-list.model.interface';
import {StringJoiner} from '../joiner/string.joiner';
import {ISimpleSelectableData} from './interface/simple-selectable-data.model.interface';
import {LessorGroup} from "./lessor-group.model";

export class Company extends LegalEntity {
  private readonly _companyName: string;
  private readonly _nip: string;
  private readonly _regon: string;
  private _krs: string;
  private readonly _group: LessorGroup | null;
  private readonly _legalForm: CompanyLegalForm;
  private readonly _civilPartnershipPartners: ICivilPartnershipPartner[];

  constructor(company: ILegalEntity) {
    super(company);
    this._companyName = company.companyName;
    this._nip = company.nip;
    this._regon = company.regon;
    this._krs = company.krs;
    this._type = LegalEntityType.COMPANY;
    if (company.group) {
      this._group = new LessorGroup(company.group);
    }
    this._legalForm = company.legalForm || 'STANDARD';
    this._civilPartnershipPartners = company.civilPartnershipPartners || [];
  }

  get companyName(): string {
    return this._companyName;
  }

  get krs(): string {
    return this._krs;
  }

  set krs(krs: string) {
    this._krs = krs;
  }

  get nip(): string {
    return this._nip;
  }

  get regon(): string {
    return this._regon;
  }

  get group(): LessorGroup | null {
    return this._group;
  }

  get legalForm(): CompanyLegalForm {
    return this._legalForm;
  }

  get civilPartnershipPartners(): ICivilPartnershipPartner[] {
    return this._civilPartnershipPartners;
  }

  toText(): string {
    const joiner = new StringJoiner('\n');
    joiner.append(`Nazwa firmy: ${this._companyName}`);
    if (this._nip) {
      joiner.append(`NIP: ${this._nip}`);
    }
    if (this._regon) {
      joiner.append(`REGON: ${this._regon}`);
    }
    if (this._krs) {
      joiner.append(`KRS: ${this._krs}`);
    }
    joiner.append(super.toText());
    return joiner.join();
  }

  toHTML(): string {
    const joiner = new StringJoiner('<br>');
    if ((super.executionsRemarks)) {
      if(super.executionsRemarks.totalCount > 0) {
        joiner.append(`<span class="verificationError"> <b>Dłużnik ma inne procesy egzekucyjne </b></span>`)
      }
    }
    joiner.append(`Nazwa firmy: <strong>${this._companyName}</strong>`);
    if (this._nip) {
      joiner.append(`NIP: ${this._nip}`);
    }
    if (this._regon) {
      joiner.append(`REGON: ${this._regon}`);
    }
    if (this._krs) {
      joiner.append(`KRS: ${this._krs}`);
    }
    if (this._legalForm === 'CIVIL_PARTNERSHIP') {
      joiner.append('Forma prawna: <strong>Spółka cywilna</strong>');
      joiner.append(`Wspólnicy: ${this.civilPartnershipPartnersText()}`);
    }
    joiner.append(super.toHTML());
    return joiner.join();
  }

  toPreview(): string {
    const joiner = new StringJoiner('<br>');
    if ((super.executionsRemarks)) {
      if(super.executionsRemarks.totalCount > 0) {
        joiner.append(`<span class="verificationError"> <b>Dłużnik ma inne procesy egzekucyjne </b></span>`)
      }
    }
    joiner.append(`<strong>${this._companyName}</strong>`);
    if (this._nip) {
      joiner.append(`${this._nip}`);
    }
    if (this._regon) {
      joiner.append(`${this._regon}`);
    }
    if (this._krs) {
      joiner.append(`${this._krs}`);
    }
    joiner.append(super.toPreview());
    return joiner.join();
  }

  toSelectText(): string {
    return this._companyName;
  }

  private civilPartnershipPartnersText(): string {
    return this._civilPartnershipPartners
      .map(partner => partner.companyName || [partner.name, partner.secondName, partner.surname]
        .filter(value => !!value)
        .join(' '))
      .filter(value => !!value)
      .join(', ');
  }
}

export class CompanyList extends ObjectList<Company, ILegalEntity> {
  protected mapToListElement(instance: ILegalEntity): Company {
    return new Company(instance);
  }

  protected mapToSelectableItem(instance: Company): ISimpleSelectableData {
    return {
      label: instance.toSelectText(),
      value: instance.id
    };
  }
}
